import * as React from "react";
import { useAuth } from "../context/auth";
import { authManager } from "../global";
import { useDispatch } from "react-redux";
import { setBackendError } from "conference/actions";
import { Box, Typography } from "@mui/material";
import { defineMessage, FormattedMessage, IntlProvider } from "react-intl";
import { useTranslation } from "../context/translation";

// Rendered before the main application tree exists, so this component cannot rely on
// the normal Translations wrapper from App.tsx.
const FullScreenMessage = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            alignItems: "center",
            display: "flex",
            minHeight: "100vh",
            justifyContent: "center",
            padding: 2,
            textAlign: "center",
        }}
    >
        <Typography variant="h6" color="textSecondary">
            {children}
        </Typography>
    </Box>
);

const NoConferenceRole = () => (
    <FullScreenMessage>
        <FormattedMessage
            {...defineMessage({
                id: "auth.noConferenceRole",
                defaultMessage: "You don't have permission to use this service.",
            })}
        />
    </FullScreenMessage>
);

// The BackendErrorDialog lives below this component, so an error at start-up has to be reported here.
const RolesUnavailable = () => (
    <FullScreenMessage>
        <FormattedMessage
            {...defineMessage({
                id: "auth.rolesUnavailable",
                defaultMessage: "Cannot determine your permissions. Please try again later.",
            })}
        />
    </FullScreenMessage>
);

// EnsureAuthenticatedUser must stay outside Translations so auth can fetch the
// server-provided user profile before Translation.locale picks the locale.
// This no-role branch still needs localized text, so it creates a narrow
// IntlProvider from the already-fetched TranslationContext.
const Localized = ({ children }: { children: React.ReactNode }) => {
    const translations = useTranslation();

    return (
        <IntlProvider locale={translations.locale} messages={translations.translations}>
            {children}
        </IntlProvider>
    );
};

const EnsureAuthenticatedUser = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuth();
    const dispatch = useDispatch();

    if (!auth.isLoggedIn) {
        authManager.login().catch((e) => {
            dispatch(setBackendError({ code: "cant_reach_heimdall", details: e.toString() }));
        });
        return null;
    } else if (auth.rolesFetchFailed) {
        // Not the same as having no roles, the roles are simply unknown.
        return (
            <Localized>
                <RolesUnavailable />
            </Localized>
        );
    } else if (!auth.hasConferenceRole) {
        return (
            <Localized>
                <NoConferenceRole />
            </Localized>
        );
    }

    return <React.Fragment>{children}</React.Fragment>;
};

export default EnsureAuthenticatedUser;
